import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, ChevronDown, Search, Layers } from 'lucide-react';

/**
 * Shared Brand selector dropdown with autocomplete.
 * Props: value, onChange, brands (array of brand names), accentColor, testId
 */
export const BrandSelector = ({ value, onChange, brands = [], accentColor = 'cyan', testId = 'brand-select' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const filtered = brands.filter(b => b.toLowerCase().includes((search || value || '').toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        <input
          type="text" value={value}
          onChange={e => { onChange(e.target.value); setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setSearch(''); }}
          className="input-dark w-full pl-10 py-2.5 pr-8"
          placeholder="Apple, Samsung, Xiaomi..."
          autoComplete="off" data-testid={testId}
        />
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 max-h-56 overflow-auto bg-gray-900 border border-white/20 rounded-xl shadow-xl">
          {filtered.map(b => (
            <button key={b} type="button"
              onClick={() => { onChange(b); setOpen(false); setSearch(''); }}
              className={`w-full px-3 py-2 text-left text-sm text-gray-200 hover:bg-${accentColor}-500/20 hover:text-white transition-colors`}
              data-testid={`brand-opt-${b.replace(/\s+/g, '-')}`}>
              {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Shared Model selector dropdown with series grouping.
 * Props: value, onChange, models (flat array), series (dict), brand (selected brand name), accentColor, testId
 */
export const ModelSelector = ({ value, onChange, models = [], series = {}, brand = '', accentColor = 'cyan', testId = 'model-select' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const q = (search || '').toLowerCase();
  const hasSeries = series && Object.keys(series).length > 0;

  const renderGrouped = () => {
    const items = [];
    for (const [seriesName, seriesModels] of Object.entries(series)) {
      const filteredModels = q
        ? seriesModels.filter(m => m.toLowerCase().includes(q))
        : seriesModels;
      if (filteredModels.length === 0) continue;
      items.push(
        <div key={`hdr-${seriesName}`} className="sticky top-0 px-3 py-1.5 bg-gray-800/95 backdrop-blur-sm border-b border-white/5 flex items-center gap-1.5">
          <Layers className={`w-3 h-3 text-${accentColor}-400/70`} />
          <span className={`text-xs font-semibold text-${accentColor}-400`}>{seriesName}</span>
          <span className="text-xs text-gray-600 ml-1">({filteredModels.length})</span>
        </div>
      );
      for (const m of filteredModels) {
        items.push(
          <button key={m} type="button"
            onClick={() => { onChange(m); setOpen(false); setSearch(''); }}
            className={`w-full px-4 py-2 text-left text-sm text-gray-200 hover:bg-${accentColor}-500/20 hover:text-white transition-colors`}
            data-testid={`model-opt-${m.replace(/\s+/g, '-')}`}>
            {m}
          </button>
        );
      }
    }
    return items;
  };

  const renderFlat = () => {
    const filtered = q ? models.filter(m => m.toLowerCase().includes(q)) : models;
    return filtered.map(m => (
      <button key={m} type="button"
        onClick={() => { onChange(m); setOpen(false); setSearch(''); }}
        className={`w-full px-3 py-2 text-left text-sm text-gray-200 hover:bg-${accentColor}-500/20 hover:text-white transition-colors`}
        data-testid={`model-opt-${m.replace(/\s+/g, '-')}`}>
        {m}
      </button>
    ));
  };

  const hasResults = hasSeries
    ? Object.values(series).some(arr => q ? arr.some(m => m.toLowerCase().includes(q)) : arr.length > 0)
    : (q ? models.some(m => m.toLowerCase().includes(q)) : models.length > 0);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        <input
          type="text" value={value}
          onChange={e => { onChange(e.target.value); setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setSearch(''); }}
          className="input-dark w-full pl-10 py-2.5 pr-8"
          placeholder={brand ? `Modele ${brand}...` : 'Selectionnez une marque'}
          autoComplete="off" data-testid={testId}
        />
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && brand && (
        <div className="absolute z-50 w-full mt-1 max-h-72 overflow-auto bg-gray-900 border border-white/20 rounded-xl shadow-xl">
          {/* Search inside dropdown */}
          <div className="sticky top-0 bg-gray-900 p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:border-cyan-500/50 focus:outline-none"
                placeholder="Rechercher un modele..."
                autoFocus data-testid={`${testId}-search`}
              />
            </div>
          </div>
          {hasResults ? (
            hasSeries ? renderGrouped() : renderFlat()
          ) : (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">Aucun modele trouve</div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Hook to fetch and manage brands-models data from the API.
 * Returns { brands, modelsMap, seriesMap, brandsData, loading }
 */
export const useBrandsModels = (api) => {
  const [brandsData, setBrandsData] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modelsMap, setModelsMap] = useState({});
  const [seriesMap, setSeriesMap] = useState({});

  useEffect(() => {
    if (!api) return;
    api.get('/brands-models').then(res => {
      const bList = [];
      const mMap = {};
      const sMap = {};
      for (const b of res.data) {
        bList.push(b.brand);
        mMap[b.brand] = b.models || [];
        sMap[b.brand] = b.series || {};
      }
      setBrands(bList);
      setModelsMap(mMap);
      setSeriesMap(sMap);
      setBrandsData(res.data);
    }).catch(() => {});
  }, [api]);

  return { brands, modelsMap, seriesMap, brandsData };
};
